import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TackLogo } from "@/components/TackLogo";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, HelpCircle, Mail } from "lucide-react";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Tack. Vanliga frågor" },
      { name: "description", content: "Svar om Tack: kom igång, belöningar via SMS, inlösen, flera salonger, personalåtkomst, integritet och betalning." },
      { property: "og:title", content: "Tack. Vanliga frågor" },
      { property: "og:description", content: "Svar om Tack: kom igång, belöningar, inlösen, flera salonger, personal och integritet." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company/faq" },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: sections.flatMap((section) =>
            section.items.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            }))
          ),
        }),
      },
    ],
  }),
  component: FaqPage,
});

type QA = { q: string; a: string };

const sections: { title: string; items: QA[] }[] = [
  {
    title: "Availability",
    items: [
      {
        q: "Which countries is Tack available in?",
        a: "Tack is currently available to merchants in Sweden. Our SMS delivery is optimized for Swedish phone numbers today, and the app is available in Swedish and English. We're planning to expand to more markets — email hello@tack.company if you'd like us to notify you when we launch in yours.",
      },
    ],
  },
  {
    title: "Getting started",
    items: [
      {
        q: "How quickly can I set up Tack.?",
        a: "Most merchants are live in under 10 minutes. Sign up, fill in your business name and discount, print your poster, and you're running.",
      },
      {
        q: "Do I need any hardware or POS integration?",
        a: "No. Tack. works with the QR poster or card you print yourself, and a phone or tablet for the cashier when redeeming. Nothing to install on your POS.",
      },
      {
        q: "What does the customer actually see when they scan?",
        a: "A clean branded page asking for a star rating and an optional comment. They enter their email and instantly receive a discount code for their next visit.",
      },
      {
        q: "Can I test it before going live?",
        a: "Yes — try the live demo from the homepage. It walks you through a real scan, rating and reward flow without affecting your account.",
      },
    ],
  },
  {
    title: "Pricing & trial",
    items: [
      {
        q: "Do you offer a free trial?",
        a: "Yes — 30 days free. Creating an account is free with no time limit: set up your brand, discounts and QR poster, and order a starter kit. When you're ready, you start the 30-day free trial by picking a plan and adding a card. Nothing is charged until day 31, and you can cancel any time before then.",
      },
      {
        q: "When does my trial actually start?",
        a: "Only when you explicitly start it — you pick a plan and add a card, and the 30 days begin from that moment. There's no hidden clock running while you set things up, so take as long as you like before starting. Sending reward SMS and collecting customers requires an active trial or subscription.",
      },

      {
        q: "How do included reward messages work across my locations?",
        a: "Your monthly included messages are a single shared pool across every location under your Growth subscription — spend them wherever you need. Solo includes 150/mo, Standard 400, and Growth 800. Each extra Growth location adds another 800 included messages to the same pool.",
      },
      {
        q: "What happens if I run out of included messages?",
        a: "You can turn on top-ups in Settings so reward delivery keeps working — +100 messages for 150 kr on Solo, 120 kr on Standard, or 90 kr on Growth (excl. VAT), with a monthly pack cap you control. Top-ups never expire and are used after your monthly allowance. If top-ups are off we give you a 3-day grace window and then pause outgoing reward SMS until the next monthly slice or you buy a pack manually.",
      },
      {
        q: "Is Tack a bulk-SMS or marketing tool?",
        a: "No. Tack is a customer loyalty product for local businesses. Messages are transactional — the customer's own one-time discount code and an optional reminder before that code expires — sent under consent captured at the moment of interaction. Bulk marketing to purchased or imported lists is not supported and violates our terms.",
      },
      {
        q: "How do top-ups work?",
        a: "Top-ups are an opt-in setting on paid plans. When you use your monthly included messages, we automatically add a 100-message top-up so reward delivery keeps going — 150 kr per pack on Solo, 120 kr on Standard, 90 kr on Growth (excl. VAT). You set a maximum number of packs per billing period (default 10), so a sudden traffic spike can never blow up your card. Each pack is a one-time charge through Stripe, shown in your top-up history in Settings, and unused pack messages carry over indefinitely.",
      },
      {
        q: "Who am I buying from, and what shows up on my statement?",
        a: "Lineri AB (Tack) is the seller for all plans and for the starter kit. Stripe processes the payment and handles VAT calculation, invoicing, receipts and payment-level disputes on our behalf. Subscription charges appear on your bank or card statement as LINK.COM* TACK; starter kit and one-off charges appear as TACK.COMPANY.",
      },
      {
        q: "Can I switch plans or cancel anytime?",
        a: "Yes. Upgrades are pro-rated, and you can cancel at any time — your subscription stays active until the end of the current billing period.",
      },
    ],
  },
  {
    title: "How it works",
    items: [
      {
        q: "How does the discount/reward get delivered?",
        a: "Right after the customer rates you, we email them a one-time discount code. They show it on their next visit, and your cashier redeems it in seconds.",
      },
      {
        q: 'What is the "Visual Burn" timer and why does it exist?',
        a: "Visual Burn is a short countdown the customer shows the cashier when redeeming. It prevents screenshots and reuse without forcing you to integrate anything into your POS.",
      },
      {
        q: "How do reward-expiry reminders work?",
        a: "If a customer's discount code is close to expiring and they haven't redeemed it, Tack sends a short reminder — under the same transactional consent captured when they scanned. It's not a marketing message; it's a heads-up about their own reward. You can turn it off in Settings.",
      },
      {
        q: "Can I customize the poster, card and email copy?",
        a: "Yes. Every customer-facing string — poster, card, scan page, emails — is editable in Settings, with a language picker and a Reset to defaults button if you want to start over.",
      },
    ],
  },
  {
    title: "Rewards & margin protection",
    items: [
      {
        q: "Won't I just give out discounts forever to the same regulars?",
        a: "No. Tack. uses a Reward Frequency cap — you decide how many discount codes a single customer can earn in a rolling time window. For example, a café might allow 3 rewards per 30 days, while a hair salon might allow 1 per 60 days. Once a customer hits that cap, the form thanks them but doesn't issue another code until the window rolls forward. Your regulars keep coming — they just stop costing you a discount on every visit.",
      },
      {
        q: "How do I pick the right limit for my business?",
        a: "When you sign up, you choose a business type (Café, Boutique, Professional service, Salon, Retail, or Other) and Tack. pre-fills a sensible default — e.g. Café = 3 per 30 days, Boutique = 2 per 60 days, Professional = 1 per 180 days. You can change it anytime in Settings → Reward frequency with two simple sliders: how many rewards per customer, and over how many days.",
      },
      {
        q: "What if a customer scans more often than the cap allows?",
        a: "They can still scan and leave a rating — that feedback is valuable. They just won't get a new discount code until they're back inside their allowance. They see a friendly message explaining when their next reward unlocks, so there's no confusion at the counter.",
      },
      {
        q: "Can I work out my worst-case discount cost in advance?",
        a: "Yes, and we recommend it. Your maximum monthly discount exposure per customer is roughly: (rewards allowed in the window ÷ window length in days) × 30 × your discount value. So 3 rewards per 30 days at a 10% off €15 average ticket = at most €4.50/month per active customer — and only if they actually come back that often. The cap is a ceiling, not a target.",
      },
      {
        q: "Do opt-in re-engagement offers count against the customer's reward limit?",
        a: "No. Re-engagement offers to existing consenting customers are separate from the QR reward loop. Re-engagement is opt-in only and goes to customers who already interacted with your business and agreed to hear about future offers — never to purchased or imported lists.",
      },
      {
        q: "Can I turn the cap off completely?",
        a: "Yes — set rewards per customer to \"Unlimited\" in Settings. We don't recommend it for most businesses (it's how loyalty programs leak margin), but it's there if your model needs it, e.g. a coffee shop running a punch-card style promotion.",
      },
    ],
  },
  {
    title: "Multiple locations & staff",
    items: [
      {
        q: "Can I run Tack. across multiple locations?",
        a: "At launch each plan covers one location. Need multiple locations? Talk to us about Enterprise.",

      },
      {
        q: "Can I give my staff cashier-only access?",
        a: "Yes. Invite staff from Settings as cashiers — they get a focused redemption screen and can't see analytics, billing or settings.",
      },
      {
        q: "How does the cashier approval queue work?",
        a: "When a customer presents a code, it appears in the cashier's live queue. The cashier taps to approve, the Visual Burn timer runs, and the code is marked redeemed.",
      },
    ],
  },
  {
    title: "Privacy & data",
    items: [
      {
        q: "What customer data does Tack. collect?",
        a: "Just the customer's email, their star rating, and an optional comment. No names, no phone numbers, no tracking across other sites.",
      },
      {
        q: "Is customer email used for anything besides the reward?",
        a: "Only the reward email and (if enabled) the We Miss You nudge from your business. Customers can unsubscribe with one click and we never share or sell their data.",
      },
      {
        q: "Where is data stored?",
        a: "Tack. is built in Sweden and your data is hosted on EU infrastructure, in line with GDPR.",
      },
      {
        q: "Do you offer a DPA for our procurement team?",
        a: "Yes. Our standard DPA is published at /dpa and is incorporated by reference into our Terms — no separate signature required for most merchants. If your procurement team needs a countersigned PDF, email hello@tack.company.",
      },
    ],
  },
];

function FaqPage() {
  const { t } = useTranslation();
  const translatedSections = (t("faq.sections", { returnObjects: true }) as typeof sections | string) as
    | typeof sections
    | string;
  const displaySections: typeof sections = Array.isArray(translatedSections) ? translatedSections : sections;
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" aria-label="Tack home" className="hover:opacity-80 transition-opacity">
            <TackLogo size="md" showSlogan />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/10 hover:text-primary">{t('common.home')}</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm">{t('common.getStarted')}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-tack-warm text-sm font-medium text-primary px-4 py-1.5 rounded-full mb-4">
            <HelpCircle size={14} />
            {t('faq.eyebrow')}
          </div>
          <h1 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight">
            {t('faq.title')}
          </h1>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            {t('faq.contactLead')}{" "}
            <a href="mailto:hello@tack.company" className="text-primary hover:underline">
              hello@tack.company
            </a>{" "}
            {t('faq.contactTrail')}
          </p>
        </div>
      </header>

      {/* Sections */}
      <section className="px-4 pb-16">
        <div className="max-w-2xl mx-auto space-y-12">
          {displaySections.map((section) => (
            <div key={section.title}>
              <h2 className="text-xl font-display font-bold mb-3">{section.title}</h2>
              <Accordion type="single" collapsible className="w-full">
                {section.items.map((item, i) => (
                  <AccordionItem key={i} value={`${section.title}-${i}`}>
                    <AccordionTrigger className="text-left font-medium">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          {/* Bottom CTA */}
          <div className="mt-12 rounded-2xl border border-border bg-tack-warm/60 p-8 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Mail size={22} className="text-primary" />
            </div>
            <h2 className="text-2xl font-display font-bold mb-2">{t('faq.stillTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t('faq.stillBody')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="mailto:hello@tack.company">
                <Button variant="outline" size="lg" className="gap-2">
                  <Mail size={16} /> hello@tack.company
                </Button>
              </a>
              <Link to="/signup">
                <Button size="lg" className="gap-2 px-8">
                  {t('common.startFreeTrial')} <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter variant="marketing" />
    </div>
  );
}
